import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { competitiveExamsApi, errorBookApi } from '../lib/api';
import { getExamPalette } from '../lib/examThemes';
import { toast } from 'sonner';
import {
  ArrowLeft, Sun, Moon, FileText, Timer, CheckCircle2,
  Loader2, AlertTriangle, ChevronLeft, ChevronRight, Flag,
  Target, Zap, Sparkles, Trophy, Crown, Medal,
} from 'lucide-react';

// Sectional NTA-style Mock Test runner.
// Flow:  setup → generating (poll) → taking → submitted (results)
export default function MockTest() {
  const [params] = useSearchParams();
  const examIdParam = params.get('examId') || 'jee_main';
  const yearParam = params.get('year') ? parseInt(params.get('year'), 10) : null;

  const { logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();

  // Phase 40.11 — Back button always returns to the selected competitive
  // exam's detail page (e.g. JEE Main hub) instead of a stale history entry.
  const goBackToExam = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    navigate(`/competitive-exams?examId=${examIdParam}`);
  }, [navigate, examIdParam]);

  // Phase 40.13 — exam-specific theme skin.
  const palette = getExamPalette(examIdParam);

  const [phase, setPhase] = useState('starting'); // starting | generating | taking | submitted
  const [jobId, setJobId] = useState(null);
  const [mock, setMock] = useState(null);
  const [jobProgress, setJobProgress] = useState({ done: 0, total: 3 });
  const [answers, setAnswers] = useState({});          // {sectionIdx-qIdx: optionIdx}
  const [marked, setMarked] = useState({});            // {sectionIdx-qIdx: true} for review
  const [activeSection, setActiveSection] = useState(0);
  const [activeQ, setActiveQ] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [leaderboard, setLeaderboard] = useState(null);
  const timerRef = useRef(null);

  const kickoff = useCallback(async () => {
    setPhase('generating');
    try {
      const job = await competitiveExamsApi.generateMock(examIdParam, yearParam);
      setJobId(job.job_id);
      if (job.expected_sections) setJobProgress({ done: 0, total: job.expected_sections });
      if (job.status === 'ready' && job.result) {
        // exam without NTA_MOCK_CONFIG returns ready immediately
        setMock(job.result);
        setTimeLeft((job.result.time_minutes || 60) * 60);
        setPhase('taking');
      }
    } catch {
      toast.error('Failed to start mock test');
      goBackToExam();
    }
  }, [examIdParam, yearParam, goBackToExam]);

  useEffect(() => { kickoff(); }, [kickoff]);

  // Polling loop for job status
  useEffect(() => {
    if (phase !== 'generating' || !jobId) return;
    const startTs = Date.now();
    const MAX_WAIT_MS = 5 * 60 * 1000; // 5 min hard stop
    const poll = async () => {
      try {
        const job = await competitiveExamsApi.pollMockJob(jobId);
        if (typeof job.progress === 'number' && typeof job.total_sections === 'number') {
          setJobProgress({ done: job.progress, total: job.total_sections });
        }
        if (job.status === 'ready' && job.result) {
          setMock(job.result);
          setTimeLeft(job.result.time_minutes * 60);
          setPhase('taking');
          return;
        }
        if (job.status === 'error') {
          toast.error('Generation failed. Please retry.');
          goBackToExam();
          return;
        }
        if (Date.now() - startTs > MAX_WAIT_MS) {
          toast.error('Generation took too long. Please retry.');
          goBackToExam();
        }
      } catch (e) {
        // keep polling
      }
    };
    const id = setInterval(poll, 4000);
    poll();
    return () => clearInterval(id);
  }, [phase, jobId, goBackToExam]);

  // Countdown timer
  useEffect(() => {
    if (phase !== 'taking') return;
    timerRef.current = setInterval(() => {
      setTimeLeft(t => {
        if (t <= 1) {
          clearInterval(timerRef.current);
          submit();
          return 0;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [phase]);  // eslint-disable-line react-hooks/exhaustive-deps

  const submit = () => {
    setPhase('submitted');
    if (timerRef.current) clearInterval(timerRef.current);
    // Log wrong answers to error book
    mock?.sections?.forEach((sec, si) => {
      sec.questions.forEach((q, qi) => {
        const userIdx = answers[`${si}-${qi}`];
        if (userIdx !== undefined && userIdx !== q.correct) {
          errorBookApi.log({
            exam_id: mock.exam_id,
            subject: sec.name,
            topic: q.topic || sec.name,
            question: q.question,
            options: q.options || [],
            user_answer: q.options?.[userIdx],
            correct_answer: q.options?.[q.correct],
            explanation: q.explanation,
            source: 'mock',
          });
        }
      });
    });
    // Save score for leaderboard
    const r = computeResults();
    competitiveExamsApi.submitScore(mock.exam_id, {
      mode: 'mock',
      score: r.correct,
      total: mock.total_questions,
      percentage: mock.max_score > 0 ? (r.score / mock.max_score) * 100 : 0,
      correct: r.correct,
      wrong: r.wrong,
      time_taken_seconds: mock.time_minutes * 60 - timeLeft,
    })
      .then(() => competitiveExamsApi.getLeaderboard(mock.exam_id, 'mock'))
      .then(lb => setLeaderboard(lb))
      .catch(() => {});
  };

  const computeResults = () => {
    if (!mock) return { correct: 0, wrong: 0, unanswered: 0, score: 0 };
    let correct = 0, wrong = 0, unanswered = 0;
    mock.sections.forEach((sec, si) => {
      sec.questions.forEach((q, qi) => {
        const pick = answers[`${si}-${qi}`];
        if (pick === undefined) unanswered++;
        else if (pick === q.correct) correct++;
        else wrong++;
      });
    });
    const score = correct * mock.marks_correct + wrong * mock.marks_wrong;
    return { correct, wrong, unanswered, score };
  };

  const fmtTime = (s) => {
    const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
    return h > 0 ? `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}` : `${m}:${String(sec).padStart(2, '0')}`;
  };

  if (phase === 'generating') {
    const pct = jobProgress.total > 0 ? Math.round(jobProgress.done * 100 / jobProgress.total) : 0;
    return (
      <div className="min-h-screen flex items-center justify-center bg-background" data-testid="mock-generating">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-950/40 flex items-center justify-center mx-auto mb-4">
            <Sparkles className="w-8 h-8 text-indigo-500 animate-pulse" />
          </div>
          <h2 className="text-xl font-bold mb-2">Building your NTA Mock Test…</h2>
          <p className="text-sm text-muted-foreground mb-6">Generating sectional questions in the style of the actual paper. Takes ~60–150 seconds.</p>
          <div className="h-2 rounded-full bg-muted overflow-hidden" data-testid="mock-progress-bar">
            <div className="h-full bg-gradient-to-r from-indigo-500 to-violet-500 transition-all duration-500" style={{ width: `${Math.max(pct, 5)}%` }} />
          </div>
          <p className="text-xs text-muted-foreground mt-2">{jobProgress.done} / {jobProgress.total} sections ready</p>
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500 mx-auto mt-6" />
        </div>
      </div>
    );
  }

  if (!mock) return null;

  if (phase === 'submitted') {
    const r = computeResults();
    const pct = mock.max_score > 0 ? Math.round((r.score / mock.max_score) * 100) : 0;
    return (
      <div className="min-h-screen bg-background text-foreground" data-testid="mock-results">
        <Header theme={theme} toggleTheme={toggleTheme} logout={logout} label={mock.mock_label} timeLeft={null} navigate={navigate} onBack={goBackToExam} palette={palette} />
        <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
          <div className="text-center py-6">
            <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full ${pct >= 60 ? 'bg-emerald-100 dark:bg-emerald-950/40' : 'bg-rose-100 dark:bg-rose-950/40'}`}>
              <CheckCircle2 className={`w-10 h-10 ${pct >= 60 ? 'text-emerald-500' : 'text-rose-500'}`} />
            </div>
            <h2 className="text-3xl font-bold mt-4">{pct}%</h2>
            <p className="text-sm text-muted-foreground">{r.score} / {mock.max_score} marks</p>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <StatBox label="Correct" value={r.correct} color="emerald" />
            <StatBox label="Wrong" value={r.wrong} color="rose" />
            <StatBox label="Skipped" value={r.unanswered} color="muted" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-testid="mock-section-results">
            {mock.sections.map((sec, si) => {
              let sc = 0, sw = 0;
              sec.questions.forEach((q, qi) => {
                const p = answers[`${si}-${qi}`];
                if (p === q.correct) sc++; else if (p !== undefined) sw++;
              });
              const sectPct = sec.questions.length ? Math.round(sc * 100 / sec.questions.length) : 0;
              return (
                <div key={si} className="p-4 rounded-xl border border-border bg-card" data-testid={`section-result-${si}`}>
                  <p className="text-xs text-muted-foreground">{sec.name}</p>
                  <p className="text-lg font-bold">{sc}/{sec.questions.length}</p>
                  <div className="h-1.5 rounded-full bg-muted overflow-hidden mt-2">
                    <div className="h-full bg-indigo-500" style={{ width: `${sectPct}%` }} />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Ranked Leaderboard */}
          {leaderboard && (
            <div className="p-5 rounded-2xl border border-amber-200 dark:border-amber-900 bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/20 dark:to-orange-950/20" data-testid="mock-leaderboard">
              <h3 className="font-bold mb-3 flex items-center gap-2">
                <Trophy className="w-4 h-4 text-amber-500" />Mock Test Leaderboard — {mock.exam_name}
              </h3>
              {leaderboard.my_rank && (
                <div className="mb-3 p-3 rounded-xl bg-indigo-500 text-white flex items-center gap-3" data-testid="mock-my-rank">
                  <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center font-bold">#{leaderboard.my_rank.rank}</div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold">{leaderboard.my_rank.user_name} (You)</p>
                    <p className="text-[11px] opacity-80">{Math.round(leaderboard.my_rank.best_percentage)}% · {leaderboard.my_rank.best_score}/{leaderboard.my_rank.best_total}</p>
                  </div>
                </div>
              )}
              <div className="space-y-1.5" data-testid="mock-leaderboard-rows">
                {(leaderboard.leaderboard || []).slice(0, 10).map((row) => {
                  const RankIcon = row.rank === 1 ? Crown : row.rank <= 3 ? Medal : null;
                  return (
                    <div key={row.user_id} className={`flex items-center gap-3 p-2.5 rounded-lg ${row.is_me ? 'bg-indigo-100 dark:bg-indigo-950/30 ring-1 ring-indigo-400' : 'bg-white dark:bg-card'} border border-border`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${row.rank === 1 ? 'bg-amber-400 text-white' : row.rank <= 3 ? 'bg-orange-300 text-orange-900' : 'bg-muted'}`}>
                        {RankIcon ? <RankIcon className="w-4 h-4" /> : `#${row.rank}`}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold truncate">{row.user_name}{row.is_me && ' (You)'}</p>
                        <p className="text-[10px] text-muted-foreground">{row.attempts} {row.attempts === 1 ? 'attempt' : 'attempts'}</p>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-bold">{Math.round(row.best_percentage)}%</p>
                        <p className="text-[10px] text-muted-foreground">{row.best_score}/{row.best_total}</p>
                      </div>
                    </div>
                  );
                })}
                {(leaderboard.leaderboard || []).length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-4">Be the first to attempt the Mock Test!</p>
                )}
              </div>
            </div>
          )}
          <div className="flex gap-3">
            <Button className="flex-1" onClick={() => navigate('/error-book')} data-testid="mock-goto-errorbook">
              <Target className="w-4 h-4 mr-1.5" />Review wrong answers in Error Book
            </Button>
            <Button variant="outline" className="flex-1" onClick={goBackToExam} data-testid="mock-exit">
              Exit
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // phase === 'taking'
  const sec = mock.sections[activeSection];
  const q = sec?.questions?.[activeQ];
  const key = `${activeSection}-${activeQ}`;
  const timeLow = timeLeft < 300; // under 5 min

  return (
    <div className="min-h-screen bg-background text-foreground" data-testid="mock-taking">
      <Header
        theme={theme} toggleTheme={toggleTheme} logout={logout}
        label={mock.mock_label}
        timeLeft={timeLeft} timeLow={timeLow}
        fmtTime={fmtTime}
        navigate={navigate}
        onBack={goBackToExam}
        onSubmit={submit}
        palette={palette}
      />
      <div className="max-w-6xl mx-auto px-6 py-4 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
        {/* Question panel */}
        <div className="space-y-4">
          <div className="flex items-center gap-2 overflow-x-auto" data-testid="mock-section-tabs">
            {mock.sections.map((s, i) => (
              <button
                key={i}
                onClick={() => { setActiveSection(i); setActiveQ(0); }}
                data-testid={`section-tab-${i}`}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition ${activeSection === i ? 'bg-indigo-500 text-white' : 'bg-muted text-muted-foreground hover:bg-accent'}`}
              >
                {s.name}
              </button>
            ))}
          </div>

          <div className="p-5 rounded-2xl border border-border bg-card" data-testid="mock-question-panel">
            <div className="flex items-center justify-between mb-3">
              <Badge variant="outline">Q{activeQ + 1} of {sec.questions.length}</Badge>
              {q?.difficulty && <Badge className={q.difficulty === 'hard' ? 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400' : q.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'}>{q.difficulty}</Badge>}
            </div>
            <p className="text-base font-medium mb-4">{q?.question}</p>
            <div className="space-y-2">
              {q?.options?.map((opt, oi) => {
                const selected = answers[key] === oi;
                return (
                  <button
                    key={oi}
                    onClick={() => setAnswers(a => ({ ...a, [key]: oi }))}
                    data-testid={`mock-option-${oi}`}
                    className={`w-full text-left p-3 rounded-lg border-2 transition ${selected ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30' : 'border-border hover:border-muted-foreground/40'}`}
                  >
                    <span className="text-xs font-bold mr-2">{String.fromCharCode(65 + oi)}.</span>
                    <span className="text-sm">{opt}</span>
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 mt-5">
              <Button variant="outline" size="sm" onClick={() => setActiveQ(Math.max(0, activeQ - 1))} disabled={activeQ === 0} data-testid="mock-prev">
                <ChevronLeft className="w-4 h-4" />Prev
              </Button>
              <Button variant="outline" size="sm" onClick={() => setMarked(m => ({ ...m, [key]: !m[key] }))} data-testid="mock-mark-review">
                <Flag className={`w-4 h-4 mr-1 ${marked[key] ? 'fill-amber-400 text-amber-500' : ''}`} />
                {marked[key] ? 'Marked' : 'Mark for review'}
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setAnswers(a => { const n = { ...a }; delete n[key]; return n; })} disabled={answers[key] === undefined} data-testid="mock-clear">
                Clear
              </Button>
              <div className="flex-1" />
              <Button size="sm" onClick={() => {
                if (activeQ < sec.questions.length - 1) setActiveQ(activeQ + 1);
                else if (activeSection < mock.sections.length - 1) { setActiveSection(activeSection + 1); setActiveQ(0); }
              }} data-testid="mock-next">
                Next<ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>

        {/* Sidebar navigator */}
        <div className="space-y-3 lg:sticky lg:top-24 lg:self-start" data-testid="mock-navigator">
          <div className="p-4 rounded-xl border border-border bg-card">
            <p className="text-xs font-semibold mb-2">Question palette — {sec.name}</p>
            <div className="grid grid-cols-6 gap-1.5">
              {sec.questions.map((_, qi) => {
                const k = `${activeSection}-${qi}`;
                const answered = answers[k] !== undefined;
                const isMarked = marked[k];
                const isActive = qi === activeQ;
                let cls = 'bg-muted text-muted-foreground';
                if (answered) cls = 'bg-emerald-500 text-white';
                if (isMarked) cls = 'bg-amber-500 text-white';
                if (answered && isMarked) cls = 'bg-gradient-to-br from-emerald-500 to-amber-500 text-white';
                if (isActive) cls += ' ring-2 ring-indigo-500';
                return (
                  <button key={qi} onClick={() => setActiveQ(qi)} className={`h-8 rounded text-xs font-semibold transition ${cls}`} data-testid={`nav-q-${activeSection}-${qi}`}>
                    {qi + 1}
                  </button>
                );
              })}
            </div>
            <div className="mt-3 flex flex-wrap gap-2 text-[10px]">
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-emerald-500 rounded-sm" />Answered</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-500 rounded-sm" />Marked</span>
              <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 bg-muted rounded-sm" />Unattempted</span>
            </div>
          </div>
          <Button className="w-full bg-rose-500 hover:bg-rose-600" onClick={submit} data-testid="mock-submit">
            Submit Test
          </Button>
        </div>
      </div>
    </div>
  );
}

function Header({ theme, toggleTheme, logout, label, timeLeft, timeLow, fmtTime, navigate, onBack, onSubmit, palette }) {
  const p = palette || { heroBg: '', accent: 'text-indigo-500', chip: '', label: '' };
  return (
    <div className={`border-b border-border bg-gradient-to-r ${p.heroBg || 'from-transparent to-transparent'} backdrop-blur sticky top-0 z-10`}>
      <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={onBack || (() => navigate(-1))} data-testid="mock-back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2">
            <FileText className={`w-4 h-4 ${p.accent}`} />
            <h1 className="text-base font-bold">{label}</h1>
            {p.label && <span className={`text-[10px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded ${p.chip}`}>{p.label}</span>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {timeLeft !== null && (
            <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-mono font-bold ${timeLow ? 'bg-rose-100 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400 animate-pulse' : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400'}`} data-testid="mock-timer">
              {timeLow && <AlertTriangle className="w-3.5 h-3.5" />}
              <Timer className="w-3.5 h-3.5" />
              {fmtTime(timeLeft)}
            </div>
          )}
          <Button variant="ghost" size="icon" onClick={toggleTheme}>
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </Button>
          <Button variant="ghost" size="sm" onClick={logout}>Logout</Button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, color }) {
  const cls = {
    emerald: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400',
    rose: 'bg-rose-100 text-rose-700 dark:bg-rose-950/40 dark:text-rose-400',
    muted: 'bg-muted text-muted-foreground',
  }[color];
  return (
    <div className={`p-4 rounded-xl ${cls} text-center`}>
      <p className="text-2xl font-bold">{value}</p>
      <p className="text-xs">{label}</p>
    </div>
  );
}
